import React, { useEffect, useState } from "react";
import { Card, Col, Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { authServices } from "Utils/auth/services";
import snackbarUtil from "Utils/snackBarUtil";
import Loader from "Common/Loader";
import { getDomainWithoutSubdomain } from "Utils/datefilter";
import { baseURL } from "Utils/baseUrl";

const ChangePassword = () => {
  const navigate = useNavigate();
  const userData = localStorage.getItem("adminDetails");

  const [oldPasswordShow, setOldPasswordShow] = useState<boolean>(false);
  const [newPasswordShow, setNewPasswordShow] = useState<boolean>(false);
  const [confirmPasswordShow, setConfirmPasswordShow] =
    useState<boolean>(false);
  const [logo, setLogo] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const userId = userData && JSON.parse(userData);

  function processURL(url: string) {
    try {
      const urlObj = new URL(url);
      const hostParts = urlObj.hostname.split(".");
      if (hostParts.length > 2) {
        const newHost = hostParts.slice(1).join(".");
        urlObj.hostname = newHost;
      }
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch (error) {
      console.error("Invalid URL:", error);
      return null;
    }
  }

  const rootDomain = processURL(baseURL);

  const getLogoData = async () => {
    const domainName = window.location.origin?.includes("localhost")
      ? process.env.REACT_APP_API_BASEURL || ""
      : window.location.origin;
    const domainData = getDomainWithoutSubdomain(domainName);

    const data = {
      key: btoa(domainData),
    };

    const { response } = await authServices.getLogo(data);
    if (response?.status) {
      if (response?.data.hasOwnProperty("self_host")) {
        if (response?.data?.self_host) {
          const logoUrl = `${
            window.location.origin?.includes("localhost")
              ? process.env.REACT_APP_API_BASEURL + "/"
              : window.location.origin + "/api/v1/"
          }${response?.data?.description}`;
          setLogo(logoUrl);
        } else {
          setLogo(response?.data?.description);
        }
      } else {
        const logoUrl = `${
          window.location.origin?.includes("localhost")
            ? process.env.REACT_APP_API_BASEURL + "/"
            : window.location.origin + "/api/v1/"
        }${response?.data?.description}`;
        setLogo(logoUrl);
      }
    }
  };

  useEffect(() => {
    getLogoData();
    // eslint-disable-next-line
  }, []);

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    const allowedCharsRegex = /^[a-zA-Z0-9@_#$-]+$/;
    if (!allowedCharsRegex.test(password)) {
      return "Password can only contain letters, numbers, and @, _, #, &, -.";
    }
    if (!/[a-zA-Z]/.test(password)) {
      return "Password must contain at least one letter.";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number.";
    }
    return "";
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Please enter your old password"),
      newPassword: Yup.string()
        .required("Please enter a new password")
        .test("validate-password", function (value) {
          if (!value)
            return this.createError({ message: "Please enter a new password" });
          const errorMessage = validatePassword(value);
          if (errorMessage) {
            return this.createError({ message: errorMessage });
          }
          return true;
        }),
      confirmPassword: Yup.string()
        .required("Please confirm your password")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      try {
        const changePayload = {
          old_password: values?.oldPassword,
          new_password: values.newPassword,
          confirm_password: values.confirmPassword,
        };
        setIsLoading(true);
        const { response } = await authServices.changePassword(
          userId?._id,
          changePayload
        );
        setIsLoading(false);
        if (response) {
          if (response.status) {
            snackbarUtil.success(response.msg);
            if (userId?.is_change_password === 0) {
              navigate("/transaction-code");
            } else {
              localStorage.removeItem("token");
              localStorage.removeItem("adminDetails");
              localStorage.removeItem("adminRefreshToken");
              localStorage.removeItem("operatorId");
              localStorage.removeItem("userId");
              localStorage.removeItem("isNotification");
              localStorage.removeItem("notifications");
              localStorage.removeItem("columnsDateState");
              window.location.replace("/login");
              navigate("/logout");
            }
          } else {
            snackbarUtil.error(response?.msg);
          }
        }
      } catch (err) {
        console.log(err, "Error in Change Password");
      }
    },
  });

  return (
    <>
      <React.Fragment>
        <Col xxl="6" className="mx-auto">
          <Card className="mb-0 border-0 shadow-none" style={{'marginTop': '110px'}}>
            <Card.Body className="p-sm-5 m-lg-4">
              <div className="text-center">
                <h5 className="fs-3xl">Change Password</h5>
                <p className="text-muted">
                  Change your password to continue to Admin.
                </p>
              </div>
              <div className="p-2 mt-5">
                <Form onSubmit={validation.handleSubmit}>
                  {/* Old Password */}
                  <Form.Group className="mb-3" controlId="formOldPassword">
                    <Form.Label>
                      Old Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative auth-pass-inputgroup mb-3">
                      <Form.Control
                        type={oldPasswordShow ? "text" : "password"}
                        className="form-control pe-5 password-input"
                        placeholder="Enter old password"
                        name="oldPassword"
                        value={validation.values.oldPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        isInvalid={
                          validation.touched.oldPassword &&
                          !!validation.errors.oldPassword
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validation.errors.oldPassword}
                      </Form.Control.Feedback>
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                        onClick={() => setOldPasswordShow(!oldPasswordShow)}
                      >
                        <i
                          className={`${
                            oldPasswordShow
                              ? "ri-eye-fill"
                              : "ri-eye-off-fill"
                          } align-middle`}
                        ></i>
                      </Button>
                    </div>
                  </Form.Group>

                  {/* New Password */}
                  <Form.Group className="mb-3" controlId="formNewPassword">
                    <Form.Label>
                      New Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative auth-pass-inputgroup mb-3">
                      <Form.Control
                        type={newPasswordShow ? "text" : "password"}
                        className="form-control pe-5 password-input"
                        placeholder="Enter new password"
                        name="newPassword"
                        value={validation.values.newPassword}
                        onChange={(e) => {
                          const { value } = e.target;
                          validation.setFieldValue("newPassword", value);

                          // Validate password dynamically
                          const errorMessage = validatePassword(value);
                          if (errorMessage) {
                            validation.setFieldError(
                              "newPassword",
                              errorMessage
                            );
                          } else {
                            validation.setFieldError("newPassword", ""); // Clear error if valid
                          }
                        }}
                        onBlur={validation.handleBlur}
                        isInvalid={
                          validation.touched.newPassword &&
                          !!validation.errors.newPassword
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validation.errors.newPassword}
                      </Form.Control.Feedback>
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                        onClick={() => setNewPasswordShow(!newPasswordShow)}
                      >
                        <i
                          className={`${
                            newPasswordShow
                              ? "ri-eye-fill"
                              : "ri-eye-off-fill"
                          } align-middle`}
                        ></i>
                      </Button>
                    </div>
                  </Form.Group>

                  {/* Confirm Password */}
                  <Form.Group
                    className="mb-3"
                    controlId="formConfirmPassword"
                  >
                    <Form.Label>
                      Confirm Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative auth-pass-inputgroup mb-3">
                      <Form.Control
                        type={confirmPasswordShow ? "text" : "password"}
                        className="form-control pe-5 password-input"
                        placeholder="Confirm new password"
                        name="confirmPassword"
                        value={validation.values.confirmPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        isInvalid={
                          validation.touched.confirmPassword &&
                          !!validation.errors.confirmPassword
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validation.errors.confirmPassword}
                      </Form.Control.Feedback>
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                        onClick={() =>
                          setConfirmPasswordShow(!confirmPasswordShow)
                        }
                      >
                        <i
                          className={`${
                            confirmPasswordShow
                              ? "ri-eye-fill"
                              : "ri-eye-off-fill"
                          } align-middle`}
                        ></i>
                      </Button>
                    </div>
                  </Form.Group>

                  {/* Submit Button */}
                  <div className="mt-4">
                    <Button className="btn btn-primary w-100" type="submit">
                      Change Password
                    </Button>
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </React.Fragment>
      {isLoading && <Loader />}
    </>
  );
};

export default ChangePassword;
